import { apiClient, unwrap, unwrapEnvelope } from '@/lib/apiClient'

/**
 * Every API call in the application lives here as a plain exported function.
 * Components never call these directly — they go through TanStack Query
 * (`useQuery` / `useMutation`) so caching, retries and loading state are
 * handled in one consistent way.
 *
 * This file is also the single place where the server's response shapes are
 * written down. The app is plain JavaScript and cannot import the API's zod
 * contracts, so if a shape drifts it drifts here and nowhere else — keep the
 * unwrapping in this module rather than reaching into `response.data` from a
 * component.
 *
 * Endpoint contract, all relative to VITE_API_BASE_URL (`/v1`):
 *
 *   POST /auth/login        { email, password }  -> { user, access_token, expires_in }
 *   POST /auth/refresh                           -> { user, access_token, expires_in }
 *   POST /auth/password     { currentPassword, newPassword }
 *   POST /auth/dev-login    { email }             -> dev only, 404 in production
 *   GET  /auth/me                                -> { user }
 *   POST /auth/logout
 *   GET  /models                                 -> { data: CapabilityManifest[] }
 *   POST /generations                            -> { id, status, deduplicated }
 *   GET  /generations?cursor&limit&status        -> { data: Generation[], meta }
 *   GET  /generations/:id                        -> Generation
 *   POST /sessions           { title?, modelKey? }   -> Session
 *   GET  /sessions                                  -> { data: Session[] }
 *   GET  /sessions/:id                              -> Session & { turns }
 *   POST /assets             <file bytes>           -> { id, mimeType, byteSize, url }
 *   GET  /admin/usage/tokens?from&to&groupBy&userId&limit&format
 *                                                -> { data: Row[], meta: { totals, from, to } }
 *   GET  /admin/users/:id/usage                  -> { quotas, tokensByModel, window }
 *   GET  /admin/users?query&role&page&limit      -> { data: User[], meta }
 *   POST /admin/users        { email, displayName, role, groupIds } -> User
 *   GET  /admin/users/:id                        -> User & { groups, quotas, models }
 *   PATCH/DELETE /admin/users/:id
 *   PUT  /admin/users/:id/groups   { groupIds }
 *   PUT/DELETE /admin/users/:id/quotas/:metric/:period
 *   GET  /admin/groups                           -> { data: Group[] }
 *   POST /admin/groups       { name, description? } -> Group
 *   GET  /admin/groups/:id                       -> Group & { members, modelKeys, policies }
 *   DELETE /admin/groups/:id
 *   PUT  /admin/groups/:id/members       { userIds }
 *   PUT  /admin/groups/:id/entitlements  { modelKeys }
 *   PUT/DELETE /admin/groups/:id/quotas/:metric/:period
 */

/* ------------------------------------------------------------------ auth --- */

/**
 * Signs in against the real endpoint: the server verifies an Argon2id hash,
 * returns a short-lived access token and sets a rotating refresh cookie.
 *
 * `dev-login` still exists for a seeded database but is 404 in production and
 * is no longer what the sign-in form calls.
 */
export const login = (credentials) => apiClient.post('/auth/login', credentials).then(unwrap)

/** Exchanges a stored token for the current user; used on cold app start. */
export const validateSession = () => apiClient.get('/auth/me').then(unwrap)

export const logout = () => apiClient.post('/auth/logout').then(() => true)

/* ----------------------------------------------------------------- usage --- */

/** Serialises only the params the caller set; the API applies its own defaults. */
const usageParams = ({ from, to, groupBy, userId, limit } = {}) => ({
  from: from || undefined,
  to: to || undefined,
  groupBy: groupBy || undefined,
  userId: userId || undefined,
  limit: limit || undefined,
})

const snakeToCamel = (key) => key.replace(/_([a-z])/g, (_, char) => char.toUpperCase())

/** Columns Postgres reports as bigint/numeric, which arrive over the wire as strings. */
const NUMERIC_FIELDS = new Set([
  'inputTokens',
  'outputTokens',
  'cachedInputTokens',
  'totalTokens',
  'requestCount',
  'estimatedCostUsd',
  'creditsCharged',
  'limit',
  'used',
  'reserved',
  'remaining',
  'concurrencyLimit',
])

/**
 * Normalises one report row.
 *
 * The usage endpoints stream raw query results, so rows arrive in the
 * database's snake_case while the envelope's `meta` is already camelCase. On
 * top of that, `bigint` and `numeric` columns arrive as *strings* — passing
 * those to a chart silently sorts and stacks them lexically ("9" > "10"), and
 * formatNumber renders them as an em dash. Both problems are fixed once, here.
 */
const normalizeRow = (row) =>
  Object.fromEntries(
    Object.entries(row ?? {}).map(([key, value]) => {
      const camel = snakeToCamel(key)
      if (NUMERIC_FIELDS.has(camel) && value !== null && value !== undefined) {
        return [camel, Number(value)]
      }
      return [camel, value]
    }),
  )

/**
 * Token usage over a window. Returns the envelope, not just the rows: the
 * summary tiles read `meta.totals`, which the server computes across the whole
 * window rather than the returned page.
 */
export const getTokenUsage = ({ signal, ...params } = {}) =>
  apiClient
    .get('/admin/usage/tokens', { params: usageParams(params), signal })
    .then(unwrapEnvelope)
    .then(({ data, meta }) => ({ rows: data.map(normalizeRow), meta }))

/** Everything about one user: live quota position plus tokens by model. */
export const getUserUsage = (userId, { signal } = {}) =>
  apiClient.get(`/admin/users/${userId}/usage`, { signal }).then((response) => {
    const body = response.data ?? {}
    return {
      // Quota rows are assembled in JS by the route and are already camelCase,
      // but their numbers come straight off a numeric column as strings.
      quotas: (body.quotas ?? []).map(normalizeRow),
      tokensByModel: (body.tokensByModel ?? []).map(normalizeRow),
      window: body.window ?? {},
    }
  })

/**
 * Downloads the same report as CSV.
 *
 * The browser cannot attach an Authorization header to a plain link, so the
 * file is fetched through the client and handed to the user as a blob.
 */
export const downloadTokenUsageCsv = async (params = {}) => {
  const response = await apiClient.get('/admin/usage/tokens', {
    params: { ...usageParams(params), format: 'csv' },
    responseType: 'blob',
  })

  const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }))
  const link = document.createElement('a')
  link.href = url
  link.download = 'token-usage.csv'
  document.body.appendChild(link)
  link.click()
  link.remove()
  // Revoking immediately can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
  return true
}

/* ---------------------------------------------------------------- models --- */

/**
 * Capability manifests for every model the caller may use. Already filtered
 * server-side by entitlement and circuit-breaker state, with admin parameter
 * caps merged over the manifest defaults.
 */
export const getModels = ({ signal } = {}) => apiClient.get('/models', { signal }).then(unwrap)

/* ----------------------------------------------------------- generations --- */

/**
 * Submits a generation. Returns immediately with an id to poll -- the server
 * queues the work rather than holding the request open.
 */
export const createGeneration = (input) =>
  apiClient.post('/generations', input).then((response) => response.data)

export const getGeneration = (generationId, { signal } = {}) =>
  apiClient.get(`/generations/${generationId}`, { signal }).then((response) => response.data)

export const listGenerations = ({ cursor, limit, status, modelKey, signal } = {}) =>
  apiClient
    .get('/generations', {
      params: {
        cursor: cursor || undefined,
        limit: limit || undefined,
        status: status || undefined,
        modelKey: modelKey || undefined,
      },
      signal,
    })
    .then((response) => response.data)

/* -------------------------------------------------------------- sessions --- */

export const createSession = (input = {}) =>
  apiClient.post('/sessions', input).then((response) => response.data)

export const listSessions = ({ signal } = {}) =>
  apiClient.get('/sessions', { signal }).then((response) => response.data)

/** The thread and every turn in it, in one call. */
export const getSession = (sessionId, { signal } = {}) =>
  apiClient.get(`/sessions/${sessionId}`, { signal }).then((response) => response.data)

/* ---------------------------------------------------------------- assets --- */

/**
 * Uploads a file and returns the completed asset.
 *
 * The bytes go to our own API, which writes them to object storage. The
 * browser used to PUT them straight to a presigned URL, which meant every
 * deployment needed a bucket CORS rule matching its exact origin -- and a
 * mismatch surfaced here as an unexplained "Failed to fetch".
 */
export const uploadAsset = async (file) => {
  const { data } = await apiClient.post('/assets', file, {
    headers: { 'content-type': file.type },
    // The bytes cross the API now, so the client's 20s default is not enough
    // for a large file on a slow connection.
    timeout: 120000,
  })
  return data
}


/* ------------------------------------------------------------------ admin --- */

export const listUsers = ({ query, role, page, limit, signal } = {}) =>
  apiClient
    .get('/admin/users', {
      params: {
        query: query || undefined,
        role: role || undefined,
        page: page || undefined,
        limit: limit || undefined,
      },
      signal,
    })
    .then((response) => response.data)

export const getUser = (userId, { signal } = {}) =>
  apiClient.get(`/admin/users/${userId}`, { signal }).then((response) => response.data)

export const createUser = (input) =>
  apiClient.post('/admin/users', input).then((response) => response.data)

export const updateUser = (userId, patch) =>
  apiClient.patch(`/admin/users/${userId}`, patch).then((response) => response.data)

export const deleteUser = (userId) => apiClient.delete(`/admin/users/${userId}`).then(() => true)

/** Admin sets or resets someone else's password. */
export const setUserPassword = (userId, password) =>
  apiClient.put(`/admin/users/${userId}/password`, { password }).then((response) => response.data)

/** Changes your OWN password; requires the current one. */
export const changeOwnPassword = (currentPassword, newPassword) =>
  apiClient.post('/auth/password', { currentPassword, newPassword }).then((response) => response.data)

export const setUserGroups = (userId, groupIds) =>
  apiClient.put(`/admin/users/${userId}/groups`, { groupIds }).then((response) => response.data)

/**
 * A quota override. Clearing one DELETEs the row rather than sending a null
 * limit — removing the user-scoped policy is what lets the group's win again.
 */
export const setUserQuota = (userId, metric, period, values) =>
  apiClient
    .put(`/admin/users/${userId}/quotas/${metric}/${period}`, values)
    .then((response) => response.data)

export const clearUserQuota = (userId, metric, period) =>
  apiClient.delete(`/admin/users/${userId}/quotas/${metric}/${period}`).then(() => true)

export const listGroups = ({ signal } = {}) =>
  apiClient.get('/admin/groups', { signal }).then((response) => response.data)

export const getGroup = (groupId, { signal } = {}) =>
  apiClient.get(`/admin/groups/${groupId}`, { signal }).then((response) => response.data)

export const createGroup = (input) =>
  apiClient.post('/admin/groups', input).then((response) => response.data)

export const deleteGroup = (groupId) =>
  apiClient.delete(`/admin/groups/${groupId}`).then(() => true)

export const setGroupMembers = (groupId, userIds) =>
  apiClient.put(`/admin/groups/${groupId}/members`, { userIds }).then((response) => response.data)

export const setGroupEntitlements = (groupId, modelKeys) =>
  apiClient
    .put(`/admin/groups/${groupId}/entitlements`, { modelKeys })
    .then((response) => response.data)

/**
 * Query key factory. Keys live beside the functions they describe so a cache
 * entry can always be traced back to the call that fills it.
 */
export const queryKeys = {
  usage: {
    all: ['usage'],
    tokens: (params) => ['usage', 'tokens', params],
    user: (userId) => ['usage', 'user', userId],
  },
  models: {
    all: ['models'],
    list: () => ['models', 'list'],
  },
  sessions: {
    all: ['sessions'],
    list: () => ['sessions', 'list'],
    detail: (id) => ['sessions', 'detail', id],
  },
  generations: {
    all: ['generations'],
    list: (params) => ['generations', 'list', params],
    detail: (id) => ['generations', 'detail', id],
  },
  people: {
    all: ['people'],
    users: (params) => ['people', 'users', params],
    user: (id) => ['people', 'user', id],
    groups: () => ['people', 'groups'],
    group: (id) => ['people', 'group', id],
  },
}
